import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, CheckCircle, AlertTriangle, RefreshCw, Send, Eye, EyeOff, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/utils/api';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const WhatsAppSettingsPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [config, setConfig] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [tokenInput, setTokenInput] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [wabaInput, setWabaInput] = useState('');
  const [testNumber, setTestNumber] = useState('');

  const loadConfig = useCallback(async () => {
    try {
      const [statusRes, templatesRes] = await Promise.all([
        axios.get(`${API}/whatsapp/status`),
        axios.get(`${API}/whatsapp/templates`).catch(() => ({ data: [] })),
      ]);
      setConfig(statusRes.data);
      setPhoneInput(statusRes.data.phone_number_id || '');
      setTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const saveConfig = async () => {
    if (!tokenInput && !phoneInput && !wabaInput) {
      toast.error('Aucune modification');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/whatsapp/config`, {
        ...(tokenInput && { access_token: tokenInput }),
        ...(phoneInput && { phone_number_id: phoneInput }),
        ...(wabaInput && { business_account_id: wabaInput }),
      });
      toast.success('Configuration WhatsApp mise a jour');
      setTokenInput('');
      loadConfig();
    } catch (err) {
      toast.error('Erreur: ' + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testNumber) {
      toast.error('Entrez un numero de test');
      return;
    }
    setTesting(true);
    try {
      const res = await axios.post(`${API}/whatsapp/test`, { phone: testNumber });
      if (res.data.success) {
        toast.success('Message test envoye !');
      } else {
        toast.error('Echec: ' + (res.data.error || 'Erreur inconnue'));
      }
    } catch (err) {
      toast.error('Erreur: ' + (err.response?.data?.detail || err.message));
    } finally { setTesting(false); }
  };

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-5" data-testid="whatsapp-settings-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-green-600" />
            Parametres WhatsApp
          </h1>
          <p className="text-sm text-slate-500">Configuration de l'integration WhatsApp Business</p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-400">{t('common.loading')}</div>
        ) : (
          <>
            {/* Status Card */}
            <Card className="p-5 rounded-xl" data-testid="whatsapp-status-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold text-slate-700">Statut de la connexion</h2>
                <Button variant="outline" size="sm" onClick={loadConfig}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Verifier
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className={`p-3 rounded-lg ${config?.has_token ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {config?.has_token ? <CheckCircle className="w-4 h-4 text-green-600" /> : <AlertTriangle className="w-4 h-4 text-red-500" />}
                    <span className="text-xs font-semibold uppercase">{config?.has_token ? 'Token actif' : 'Token manquant'}</span>
                  </div>
                </div>
                <div className={`p-3 rounded-lg ${config?.configured ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {config?.configured ? <CheckCircle className="w-4 h-4 text-green-600" /> : <AlertTriangle className="w-4 h-4 text-amber-500" />}
                    <span className="text-xs font-semibold uppercase">{config?.configured ? 'Configure' : 'Non configure'}</span>
                  </div>
                </div>
              </div>
              {config?.phone_number_id && (
                <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
                  <Phone className="w-3 h-3" /> Phone ID: {config.phone_number_id}
                </p>
              )}
            </Card>

            {/* Config Card */}
            <Card className="p-5 rounded-xl" data-testid="whatsapp-config-card">
              <h2 className="text-sm font-bold text-slate-700 mb-4">Mettre a jour la configuration</h2>
              <div className="space-y-4">
                <div className="space-y-1">
                  <Label className="text-xs">Token d'acces WhatsApp</Label>
                  <div className="flex gap-2">
                    <Input
                      type={showToken ? 'text' : 'password'}
                      value={tokenInput}
                      onChange={e => setTokenInput(e.target.value)}
                      placeholder="EAAi... (collez le nouveau token ici)"
                      data-testid="whatsapp-token-input"
                    />
                    <Button variant="outline" size="sm" onClick={() => setShowToken(!showToken)}>
                      {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-[10px] text-slate-400">Genere depuis Meta Business Manager → Utilisateurs systeme → Generer un token</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Phone Number ID</Label>
                  <Input value={phoneInput} onChange={e => setPhoneInput(e.target.value)} placeholder="ex: 996582056879554" data-testid="whatsapp-phone-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">WABA ID (Business Account)</Label>
                  <Input value={wabaInput} onChange={e => setWabaInput(e.target.value)} placeholder="ex: 1480697683684693" data-testid="whatsapp-waba-input" />
                </div>
                <Button onClick={saveConfig} disabled={saving} data-testid="save-whatsapp-config">
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </div>
            </Card>

            {/* Test Card */}
            <Card className="p-5 rounded-xl" data-testid="whatsapp-test-card">
              <h2 className="text-sm font-bold text-slate-700 mb-4">Envoyer un message test</h2>
              <div className="flex gap-2">
                <Input
                  value={testNumber}
                  onChange={e => setTestNumber(e.target.value)}
                  placeholder="ex: 0795046837"
                  data-testid="whatsapp-test-number"
                />
                <Button onClick={sendTest} disabled={testing} data-testid="send-whatsapp-test">
                  <Send className="w-4 h-4 mr-1" /> {testing ? 'Envoi...' : 'Tester'}
                </Button>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">Envoie le template "vehicule_pret" au numero indique</p>
            </Card>

            {/* Templates Card */}
            {templates.length > 0 && (
              <Card className="p-5 rounded-xl" data-testid="whatsapp-templates-card">
                <h2 className="text-sm font-bold text-slate-700 mb-4">Templates disponibles</h2>
                <div className="space-y-2">
                  {templates.map((t, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{t.name}</p>
                        <p className="text-xs text-slate-500">{t.language} | {t.category}</p>
                      </div>
                      <Badge className={t.status === 'APPROVED' ? 'bg-green-100 text-green-700' : t.status === 'PENDING' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                        {t.status === 'APPROVED' ? 'Approuve' : t.status === 'PENDING' ? 'En attente' : t.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default WhatsAppSettingsPage;
